import React, { useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  notification,
  DatePicker,
  Upload,
  message,
  Space,
} from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createDrawing, createDrawingsBulkAI } from "../services/drawingsAPI";
import { useAuth } from "../hooks/useAuth";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { UploadOutlined } from "@ant-design/icons";
import "./CreateDrawingModal.css";

const MAX_BULK_PDF_COUNT = 20;
const MAX_PDF_FILE_SIZE = 150 * 1024 * 1024; // 150 MB per file
const MAX_BULK_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB total per upload

const CreateDrawingModal = ({
  visible,
  onClose,
  folderId,
  isAIMode = false,
  onProcessingStarted,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const bulkLimitWarnedRef = useRef(false);
  const bulkSizeWarnedRef = useRef(false);
  const bulkFileSizeWarnedRef = useRef(false);

  const handleCreateDrawing = async (values) => {
    if (fileList.length === 0) {
      notification.error({
        message: "Please upload a drawing file",
      });
      return;
    }

    setUploading(true);
    try {
      if (isAIMode) {
        // AI mode - upload all selected files at once for bulk processing
        const files = fileList
          .map((file) => file.originFileObj)
          .filter(Boolean);

        if (files.length === 0) {
          notification.error({
            message: "Please upload at least one PDF file",
          });
          return;
        }

        if (files.length > MAX_BULK_PDF_COUNT) {
          notification.error({
            message: `You can upload up to ${MAX_BULK_PDF_COUNT} PDFs at a time`,
          });
          return;
        }

        const oversizedFile = files.find((file) => file.size > MAX_PDF_FILE_SIZE);
        if (oversizedFile) {
          notification.error({
            message: "Each PDF must be smaller than 150 MB",
            description: `"${oversizedFile.name}" exceeds the limit.`,
          });
          return;
        }

        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_BULK_TOTAL_SIZE) {
          notification.error({
            message: "Total upload size cannot exceed 500 MB",
          });
          return;
        }

        await createDrawingsBulkAI(projectId, folderId, files);

        queryClient.invalidateQueries(["drawings", projectId, folderId]);
        // Let the list page auto-refresh while the background job runs
        onProcessingStarted?.();
        onClose();
        form.resetFields();
        setFileList([]);
        notification.success({
          message:
            files.length > 1
              ? `${files.length} PDFs processing has started. Please allow a few minutes — results will appear here automatically.`
              : "PDF processing has started. Please allow a few minutes — results will appear here automatically.",
        });
      } else {
        // Manual mode - create drawing with all fields
        const drawingData = {
          title: values.title,
          drawing_number: values.drawing_number,
          revision_date: values.revision_date.format("YYYY-MM-DD"),
          revision_title: values.revision_title,
          file: fileList[0].originFileObj,
        };

        await createDrawing(projectId, folderId, drawingData);

        queryClient.invalidateQueries(["drawings", projectId, folderId]);
        onClose();
        form.resetFields();
        setFileList([]);
        notification.success({
          message: "Drawing created successfully",
        });
      }
    } catch (error) {
      notification.error({
        message: "Failed to create drawing",
        description: error.message,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    let nextList = isAIMode ? newFileList : newFileList.slice(-1);

    if (isAIMode && nextList.length > MAX_BULK_PDF_COUNT) {
      notification.warning({
        message: `You can select up to ${MAX_BULK_PDF_COUNT} PDFs at a time`,
        description: `Only the first ${MAX_BULK_PDF_COUNT} files were kept.`,
      });
      nextList = nextList.slice(0, MAX_BULK_PDF_COUNT);
    }

    if (isAIMode) {
      const validSizeList = nextList.filter(
        (item) => (item.size || 0) <= MAX_PDF_FILE_SIZE
      );
      if (validSizeList.length < nextList.length) {
        notification.warning({
          message: "Each PDF must be smaller than 150 MB",
          description: "Oversized file(s) were not added.",
        });
        nextList = validSizeList;
      }

      let totalSize = nextList.reduce(
        (sum, item) => sum + (item.size || 0),
        0
      );
      if (totalSize > MAX_BULK_TOTAL_SIZE) {
        notification.warning({
          message: "Total upload size cannot exceed 500 MB",
          description: "Some files were removed to stay within the limit.",
        });
        while (totalSize > MAX_BULK_TOTAL_SIZE && nextList.length > 0) {
          nextList = nextList.slice(0, -1);
          totalSize = nextList.reduce(
            (sum, item) => sum + (item.size || 0),
            0
          );
        }
      }
    }

    setFileList(nextList);
  };

  const handleUploadExceed = (files) => {
    notification.warning({
      message: `You can select up to ${MAX_BULK_PDF_COUNT} PDFs at a time`,
      description: `You tried to add ${files.length} more file(s), but the limit is ${MAX_BULK_PDF_COUNT}.`,
    });
  };

  const beforeUpload = (file, batchFileList) => {
    const batchIndex = batchFileList.findIndex((f) => f.uid === file.uid);
    if (batchIndex === 0) {
      bulkLimitWarnedRef.current = false;
      bulkSizeWarnedRef.current = false;
      bulkFileSizeWarnedRef.current = false;
    }

    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      message.error("You can only upload PDF files!");
      return Upload.LIST_IGNORE;
    }
    if (file.size === 0) {
      message.error("Empty PDF files are not allowed");
      return Upload.LIST_IGNORE;
    }
    if (isAIMode && file.size > MAX_PDF_FILE_SIZE) {
      if (!bulkFileSizeWarnedRef.current) {
        bulkFileSizeWarnedRef.current = true;
        notification.warning({
          message: "Each PDF must be smaller than 150 MB",
          description: `"${file.name}" was not added.`,
        });
      }
      return Upload.LIST_IGNORE;
    }

    if (isAIMode) {
      if (batchIndex + 1 + fileList.length > MAX_BULK_PDF_COUNT) {
        if (!bulkLimitWarnedRef.current) {
          bulkLimitWarnedRef.current = true;
          const attemptedTotal = batchFileList.length + fileList.length;
          const excess = attemptedTotal - MAX_BULK_PDF_COUNT;
          notification.warning({
            message: `You can select up to ${MAX_BULK_PDF_COUNT} PDFs at a time`,
            description:
              excess > 0
                ? `${excess} extra file(s) were not added.`
                : undefined,
          });
        }
        return Upload.LIST_IGNORE;
      }

      const existingSize = fileList.reduce(
        (sum, item) => sum + (item.size || 0),
        0
      );
      const batchSizeBeforeThis = batchFileList
        .slice(0, batchIndex)
        .reduce((sum, batchFile) => sum + batchFile.size, 0);
      const projectedTotal = existingSize + batchSizeBeforeThis + file.size;

      if (projectedTotal > MAX_BULK_TOTAL_SIZE) {
        if (!bulkSizeWarnedRef.current) {
          bulkSizeWarnedRef.current = true;
          notification.warning({
            message: "Total upload size cannot exceed 500 MB",
            description: "This file was not added because it would exceed the limit.",
          });
        }
        return Upload.LIST_IGNORE;
      }
    }

    return false;
  };

  const modalTitle = isAIMode ? "AI Bulk Upload" : "Add Drawing";

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        onFinish={handleCreateDrawing}
        layout="vertical"
        className="w-full"
      >
        <Form.Item
          label={
            isAIMode
              ? "Upload Drawings for AI Processing (PDF)"
              : "Upload Drawing (PDF)"
          }
          required
          className="mb-6 upload-form-item"
        >
          <Upload
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            onExceed={isAIMode ? handleUploadExceed : undefined}
            fileList={fileList}
            multiple={isAIMode}
            maxCount={isAIMode ? MAX_BULK_PDF_COUNT : 1}
            accept=".pdf,application/pdf"
          >
            <div className="w-full border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
              <UploadOutlined className="text-2xl mb-2 text-gray-400" />
              <div>
                <p className="mb-1">
                  {isAIMode
                    ? "Click or drag files to upload"
                    : "Click or drag file to upload"}
                </p>
                <p className="text-xs text-gray-400">Support for PDF only</p>
                {isAIMode && (
                  <p className="text-xs text-blue-500 mt-1">
                    Select up to {MAX_BULK_PDF_COUNT} PDFs at once (max 150 MB
                    each, 500 MB total). AI will automatically extract drawings
                    from multi-page PDFs <br />
                    Beta Version. AI may make mistakes.
                  </p>
                )}
              </div>
            </div>
          </Upload>
        </Form.Item>

        {!isAIMode && (
          <>
            <Form.Item
              label="Title"
              name="title"
              rules={[
                { required: true, message: "Please input the drawing title!" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Drawing Number"
              name="drawing_number"
              rules={[
                { required: true, message: "Please input the drawing number!" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Revision Date"
              name="revision_date"
              rules={[
                { required: true, message: "Please select the revision date!" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format={DATE_PICKER_FORMAT} />
            </Form.Item>

            <Form.Item
              label="Revision Title"
              name="revision_title"
              rules={[
                { required: true, message: "Please input the revision title!" },
              ]}
            >
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={uploading}
            className="w-full"
          >
            {isAIMode ? "Process with AI" : "Create Drawing"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateDrawingModal;
